import { Vector } from '@/GraphEditor/helpers/Vector';
import { ReactNode, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css, keyframes, ThemeContext } from 'styled-components';
import { ExecutionErrorDisplay } from '@/GraphEditor/drawingLayer/graphComponents/ExecutionErrorDisplay';
import { ThemeType } from '@/theme/glueTheme';
import { useNodePosition } from '@/GraphEditor/drawingLayer/contexts/NodePositionContext';
import { useNodeDimensions } from '@/GraphEditor/drawingLayer/contexts/NodeDimensionsContext';
import { useNodeRuns } from '@/GraphEditor/drawingLayer/contexts/NodeRunsContext';
import { useNodeSelected } from '@/GraphEditor/drawingLayer/contexts/NodeSelectedContext';
import { useNodeExecutionError } from '@/GraphEditor/drawingLayer/contexts/NodeExecutionErrorContext';
import { useNodeAnimation } from '@/GraphEditor/contexts/NodeAnimationContext';

interface NodeContainerProps {
    position: Vector;
    $isAnimating?: boolean;
}

const Container = styled.div.attrs<NodeContainerProps>(props => ({
    style: {
        transform: `translate(${props.position.x + 'px'}, ${props.position.y + 'px'})`,
    },
}))<NodeContainerProps>`
    min-width: 150px;
    position: absolute;
    pointer-events: all;
    user-select: none;
    ${props =>
        props.$isAnimating &&
        css`
            transition: transform 300ms ease-out;
        `}
`;

interface SelectedBorderOverlayProps {
    borderWidth: number;
    borderColor: string;
}

const SelectedBorderOverlay = styled.div<SelectedBorderOverlayProps>`
    position: absolute;
    pointer-events: none;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 10px;
    box-sizing: border-box;
    border: ${props => props.borderWidth}px solid ${props => props.borderColor};
`;

const highlightFade = keyframes`
    0% {opacity: 0}
    10% {opacity: 1}
    100% {opacity: 0}
`;

const HighlightOverlay = styled.div`
    position: absolute;
    pointer-events: none;
    background-color: rgba(255, 255, 255, 0.6);
    opacity: 0;
    animation: ${highlightFade} 2s;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 10px;
`;

interface Props {
    measureSize?: boolean;
    children: ReactNode;
    ariaLabel?: string;
}

export const NodeContainer: React.FC<Props> = ({ measureSize = true, children, ariaLabel }) => {
    const fullBodySizeElementRef = useRef<HTMLDivElement>(null);
    const [measuredNodeWidth, setMeasuredNodeWidth] = useState<number>(0);
    const [measuredNodeHeight, setMeasuredNodeHeight] = useState<number>(0);
    const position = useNodePosition();
    const setNodeDimensions = useNodeDimensions();
    const runTimes = useNodeRuns();
    const selected = useNodeSelected();
    const executionError = useNodeExecutionError();
    const theme = useContext<ThemeType>(ThemeContext);
    const { isAnimating } = useNodeAnimation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        const nodeContainerElement = fullBodySizeElementRef.current;
        if (nodeContainerElement) {
            setMeasuredNodeWidth(nodeContainerElement.scrollWidth);
            setMeasuredNodeHeight(nodeContainerElement.scrollHeight);
        }
    });

    useEffect(() => {
        if (measureSize) {
            setNodeDimensions(measuredNodeWidth, measuredNodeHeight);
        }
    }, [measuredNodeWidth, measuredNodeHeight, setNodeDimensions, measureSize]);

    return (
        <Container position={position} $isAnimating={isAnimating} aria-label={ariaLabel} data-node-container>
            <SelectedBorderOverlay
                ref={fullBodySizeElementRef}
                borderWidth={selected ? 2 : 1}
                borderColor={selected ? theme.primaryContrastColor : '#3a3d42'}
            />
            {runTimes ? Array.from(runTimes).map(runTime => <HighlightOverlay key={runTime} />) : null}
            {executionError ? (
                <ExecutionErrorDisplay position={{ x: 0, y: measuredNodeHeight + 8 }}>{executionError.message}</ExecutionErrorDisplay>
            ) : null}
            {children}
        </Container>
    );
};
